import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs';

import { ProjectContextService } from '@core/project-context/project-context.service';
import { ProjectContextStore } from '@core/project-context/project-context.store';
import { Button } from '@shared/components/button/button';
import { Modal, ModalFooter } from '@shared/components/modal/modal';
import { SnackbarService } from '@shared/components/snackbar/snackbar.service';
import { FormSelect } from '@shared/form-controls/form-select/form-select';

const SWITCH_FAILED_MESSAGE = 'layout.top.changeProjectFailed';

/**
 * Picks the active project. The sidebar's Change button opens it through
 * `ModalService`; it closes with the id of the project it switched to, or
 * `undefined` when dismissed.
 *
 * The switch is persisted before the modal closes. While the request is in
 * flight the frame is covered and the dialog cannot be dismissed, and a switch
 * the backend refuses or never receives leaves the modal open on the user's
 * pick, with the active project unchanged behind it.
 */
@Component({
  selector: 'app-project-selector-modal',
  imports: [Modal, ModalFooter, Button, FormSelect, ReactiveFormsModule, TranslatePipe],
  templateUrl: './project-selector-modal.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectSelectorModal {
  protected readonly ref = inject<MatDialogRef<ProjectSelectorModal, number>>(MatDialogRef);
  private readonly projectContextStore = inject(ProjectContextStore);
  private readonly projectContextService = inject(ProjectContextService);
  private readonly snackbar = inject(SnackbarService);

  protected readonly projectOptions = computed(() =>
    this.projectContextStore.projects().map((project) => ({
      value: project.idProject,
      label: project.name,
    })),
  );

  protected readonly selectedProjectId = new FormControl(this.projectContextStore.activeProjectId());
  private readonly selectedProjectIdValue = toSignal(this.selectedProjectId.valueChanges, {
    initialValue: this.selectedProjectId.value,
  });

  /** Picking the project that is already active is not a switch. */
  protected readonly canConfirm = computed(() => {
    const projectId = this.selectedProjectIdValue();

    return projectId !== null && projectId !== this.projectContextStore.activeProjectId();
  });

  protected readonly switching = computed(() => this.projectContextStore.status() === 'loading');

  protected confirm(): void {
    const projectId = this.selectedProjectId.value;

    if (projectId === null || !this.canConfirm()) return;

    // The cover loader blocks the footer and the ✕, but not Escape or a
    // backdrop click — those would close the modal on a switch still in flight.
    this.ref.disableClose = true;

    this.projectContextService
      .setSelectedProject(projectId)
      .pipe(finalize(() => (this.ref.disableClose = false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.ref.close(projectId);
          } else {
            this.snackbar.error(SWITCH_FAILED_MESSAGE);
          }
        },
        error: () => this.snackbar.error(SWITCH_FAILED_MESSAGE),
      });
  }
}
